import React, { useEffect, useState } from 'react';
import { Campaign, CampaignStats } from '../types';
import { AdminSessionError, fetchSbicStats } from '../lib/adminApi';

interface StatsModalProps {
  campaign: Campaign;
  onClose: () => void;
}

const StatsModal: React.FC<StatsModalProps> = ({ campaign, onClose }) => {
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [recentCodes, setRecentCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadError(false);
      try {
        const data = await fetchSbicStats(campaign.id);
        setStats({
          total_issued: data.total_issued,
          total_redeemed: data.total_redeemed,
          redemption_rate: data.redemption_rate,
        });
        setRecentCodes(data.recent);
      } catch (err) {
        if (!(err instanceof AdminSessionError)) setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [campaign.id]);

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        <div style={s.header}>
          <div>
            <div style={s.title}>Redemption Stats</div>
            <div style={s.subtitle}>{campaign.advertiser_name} · {campaign.sbic_discount_code}</div>
          </div>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>

        {loading ? (
          <div style={s.loading}>Loading stats…</div>
        ) : loadError ? (
          <div style={s.empty}>Unable to load stats.</div>
        ) : (
          <>
            <div style={s.statRow}>
              <StatCard label="Codes Issued" value={stats?.total_issued || 0} />
              <StatCard label="Redeemed" value={stats?.total_redeemed || 0} accent />
              <StatCard label="Redemption Rate" value={`${(stats?.redemption_rate || 0).toFixed(1)}%`} />
            </div>

            <div style={s.tableWrap}>
              <div style={s.tableTitle}>Recent SBIC Codes</div>
              {recentCodes.length === 0 ? (
                <div style={s.empty}>No codes issued yet for this campaign.</div>
              ) : (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Code</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Issued</th>
                      <th style={s.th}>Redeemed At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentCodes.map(code => (
                      <tr key={code.id} style={s.tr}>
                        <td style={s.td}><code style={s.code}>{code.code}</code></td>
                        <td style={s.td}>
                          <span style={{ ...s.badge, ...(code.redeemed ? s.badgeGreen : s.badgeGray) }}>
                            {code.redeemed ? 'Redeemed' : 'Pending'}
                          </span>
                        </td>
                        <td style={{ ...s.td, color: '#666' }}>{new Date(code.created_at).toLocaleDateString()}</td>
                        <td style={{ ...s.td, color: '#666' }}>
                          {code.redeemed_at ? new Date(code.redeemed_at).toLocaleDateString() : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ label, value, accent }: { label: string; value: string | number; accent?: boolean }) => (
  <div style={{ ...sc.card, ...(accent ? sc.cardAccent : {}) }}>
    <div style={{ ...sc.val, ...(accent ? sc.valAccent : {}) }}>{value}</div>
    <div style={sc.label}>{label}</div>
  </div>
);

const sc: Record<string, React.CSSProperties> = {
  card: {
    flex: 1, background: '#111', border: '1px solid #222',
    borderRadius: 10, padding: '16px 20px', textAlign: 'center',
  },
  cardAccent: { borderColor: 'rgba(74,222,128,0.3)', background: 'rgba(74,222,128,0.05)' },
  val: { fontSize: 28, fontWeight: 700, color: '#e5e5e5', fontFamily: "'DM Mono', monospace" },
  valAccent: { color: '#4ade80' },
  label: { fontSize: 11, color: '#555', marginTop: 4, letterSpacing: '0.08em', textTransform: 'uppercase' },
};

const s: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, backdropFilter: 'blur(4px)',
  },
  modal: {
    background: '#1a1a1a', border: '1px solid #2a2a2a',
    borderRadius: 16, padding: 32, width: 600, maxWidth: '95vw',
    maxHeight: '85vh', overflowY: 'auto',
    fontFamily: "'DM Mono', monospace",
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { color: '#e5e5e5', fontSize: 18, fontWeight: 700 },
  subtitle: { color: '#555', fontSize: 12, marginTop: 4 },
  closeBtn: {
    background: 'transparent', border: '1px solid #2a2a2a',
    borderRadius: 6, color: '#666', width: 32, height: 32,
    cursor: 'pointer', fontSize: 13, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  },
  loading: { color: '#555', textAlign: 'center', padding: 40 },
  statRow: { display: 'flex', gap: 12, marginBottom: 28 },
  tableWrap: {},
  tableTitle: { color: '#666', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 },
  empty: { color: '#444', fontSize: 13, padding: '20px 0', textAlign: 'center' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#555', fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #222' },
  tr: {},
  td: { padding: '10px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #1a1a1a' },
  code: { background: '#0d0d0d', borderRadius: 4, padding: '2px 6px', fontSize: 12, color: '#4ade80' },
  badge: { padding: '3px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600 },
  badgeGreen: { background: 'rgba(74,222,128,0.12)', color: '#4ade80' },
  badgeGray: { background: '#222', color: '#666' },
};

export default StatsModal;